import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Switch } from "components/DesignSystem/Switch/Switch";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { EditFinancialsEmailModal } from "components/EditFinancialsEmailModal/EditFinancialsEmailModal";
import { EditPencil } from "components/icons/EditPencil";
import { CASH_ACCRUAL_BLOG_URL } from "constants/base";
import {
  ACCOUNTING_METHOD_TYPES,
  TAX_YEAR_END_TYPES,
} from "constants/bookkeeping";
import {
  ACCOUNTING_METHOD_OPTIONS,
  TAX_YEAR_OPTIONS,
} from "constants/booksSettings";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps } from "formik";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} from "store/apis/booksLockDate";
import {
  useGetStartingDateQuery,
  useUpdateStartDateMutation,
} from "store/apis/chartOfAccounts";
import { useUpdateStepFieldMutation } from "store/apis/productOnboarding";
import { StepComponent } from "types/Models/onboarding";
import { BackendError } from "types/utils/error";
import { ShareBankAccountPreferencesContent } from "./Tax/ShareBankAccountPreferences";

type BookkeepingPreferencesProps = {
  booksMode: string;
  setBooksMode: (mode: string) => void;
  fieldValues?: StepComponent[];
};

const BookkeepingSettings = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityDetail = useCurrentEntity();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();

  const [updateStartDate, { isLoading }] = useUpdateStartDateMutation();

  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId: entityId! },
    { skip: !groupId || !entityId }
  );

  const savedStartDate = startDateData?.start_date
    ? dayjs(startDateData.start_date).toDate()
    : null;

  const handleDateChange = async (
    date: Date | null,
    setValue: (value: Date | null) => void
  ) => {
    if (!date) return;

    setValue(date);

    try {
      await updateStartDate({
        groupId,
        entityId,
        startDate: dayjs(date).format(YYYY_MM_DD),
      }).unwrap();

      successToast({ message: "Starting date updated" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const { data: lockDateData } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const [updateBooksSetting, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const onAccountingMethodChange = async (
    value: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (value instanceof Array || !value) return;
    if (!entityId || value.value === lockDateData?.accounting_method) return;

    try {
      await updateBooksSetting({
        entityId,
        accounting_method: value.value as ACCOUNTING_METHOD_TYPES,
      }).unwrap();

      successToast({
        title: "Accounting method updated",
        message: `Your accounting method is now set to ${value.label}.`,
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update accounting method",
        },
        error as Error
      );
    }
  };

  const editEmailModal = useModal();
  const taxYearConfirmModal = useModal();

  const [pendingTaxYear, setPendingTaxYear] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const sendFinancialsEnabled =
    lockDateData?.send_completed_financials_email ?? false;
  const financialEmails =
    lockDateData?.completed_financials_email_recipients ?? [];

  const onSendFinancialsToggle = async (checked: boolean) => {
    if (!entityId) return;

    try {
      await updateBooksSetting({
        entityId,
        send_completed_financials_email: checked,
      }).unwrap();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update setting",
        },
        error as Error
      );
    }
  };

  const onTaxYearChange = (
    value: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (value instanceof Array || !value) return;
    if (!entityId || value.value === lockDateData?.tax_year_end) return;

    setPendingTaxYear({
      value: value.value ?? "",
      label: value.label as string,
    });
    taxYearConfirmModal.open();
  };

  const handleConfirmTaxYearChange = async () => {
    if (!entityId || !pendingTaxYear) return;

    try {
      await updateBooksSetting({
        entityId,
        tax_year_end: pendingTaxYear.value as TAX_YEAR_END_TYPES,
      }).unwrap();

      successToast({
        title: "Tax year end updated",
        message: `Your tax year end is now set to ${pendingTaxYear.label}.`,
      });
      taxYearConfirmModal.close();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update tax year end",
        },
        error as Error
      );
    }
  };

  return (
    <>
      <div className="t-flex t-flex-col t-gap-6">
        <div className="t-text-text-30 t-text-body-sm">
          Configure key settings that help you get the most out of the platform,
          from how your books are organised to what automations you’d like to
          enable.
        </div>
        <div className="t-flex t-gap-6 t-items-start sm:t-flex-col sm:t-gap-3">
          <div className="t-flex-1 t-flex t-flex-col t-gap-2">
            <label className="t-text-subtext">Bookkeeping Start Date</label>
            <p className="t-m-0 t-text-body-sm-lite t-text-text-30">
              Pick the month and year you'd like Inkle Books to begin recording
              your financial data. Usually, this matches the start of your
              financial year or the point you're migrating from another system.
            </p>
          </div>

          <div className="t-w-40 t-shrink-0 sm:t-w-full">
            <Field name="start_date">
              {({ field, form }: FieldProps) => (
                <DateInput
                  {...field}
                  value={field.value || savedStartDate}
                  onDateChange={(date) =>
                    handleDateChange(date, (val) =>
                      form.setFieldValue("start_date", val)
                    )
                  }
                  label=""
                  placeholder="Select"
                  block
                  portalId="coa-start-date"
                  disabled={isLoading}
                />
              )}
            </Field>
          </div>
        </div>

        <Divider />

        <div className="t-flex t-gap-6 t-items-start sm:t-flex-col sm:t-gap-3">
          <div className="t-flex-1 t-flex t-flex-col t-gap-2">
            <label className="t-text-subtext">Accounting Method</label>
            <p className="t-m-0 t-text-body-sm-lite t-text-text-30">
              We'll use this to determine how your revenue and expenses are
              shown everywhere.
            </p>
            <span className="t-text-body-sm t-italic t-text-text-30">
              Not sure?{" "}
              <a
                href={CASH_ACCRUAL_BLOG_URL}
                target="_blank"
                rel="noreferrer"
                className="t-text-purple t-no-underline"
              >
                Learn about Cash Vs Accrual
              </a>
            </span>
          </div>
          <div className="t-w-40 t-shrink-0 sm:t-w-full">
            <Combobox
              options={ACCOUNTING_METHOD_OPTIONS}
              value={ACCOUNTING_METHOD_OPTIONS.find(
                (method) => method.value === lockDateData?.accounting_method
              )}
              onChange={onAccountingMethodChange}
              isClearable={false}
            />
          </div>
        </div>

        <Divider />

        <div className="t-flex t-gap-6 t-items-start sm:t-flex-col sm:t-gap-3">
          <div className="t-flex-1 t-flex t-flex-col t-gap-2">
            <label className="t-text-subtext">Tax Year End</label>
            <p className="t-m-0 t-text-body-sm-lite t-text-text-30">
              We'll use this to populate your filters of current year, previous
              year etc, and will handle the retained earnings, tax deadlines.
            </p>
          </div>
          <div className="t-w-40 t-shrink-0 sm:t-w-full">
            <Combobox
              options={TAX_YEAR_OPTIONS}
              value={TAX_YEAR_OPTIONS.find(
                (year) => year.value === lockDateData?.tax_year_end
              )}
              onChange={onTaxYearChange}
              isClearable={false}
            />
          </div>
        </div>

        <Divider />

        <div className="t-flex t-gap-6 t-items-start sm:t-flex-col sm:t-gap-3">
          <div className="t-flex-1 t-flex t-flex-col t-gap-2">
            <label className="t-text-subtext">
              Send completed financials via email
            </label>
            <p className="t-m-0 t-text-body-sm-lite t-text-text-30">
              When enabled, you'll receive completed financials automatically
              once books are closed for each period.
            </p>
            {sendFinancialsEnabled && (
              <div className="t-flex t-items-center t-gap-1.5">
                {financialEmails.length > 0 && (
                  <span className="t-text-body-sm-lite t-text-text-30 t-italic">
                    {financialEmails.join(", ")}
                  </span>
                )}
                <Button
                  customType="ghost_icon"
                  size="xsmall"
                  onClick={editEmailModal.open}
                >
                  <EditPencil strokeWidth="1.2" color="currentColor" />
                </Button>
              </div>
            )}
          </div>
          <Switch
            checked={sendFinancialsEnabled}
            onCheckedChange={onSendFinancialsToggle}
          />
        </div>
      </div>

      <EditFinancialsEmailModal
        isOpen={editEmailModal.isOpen}
        onClose={editEmailModal.close}
        emails={financialEmails}
      />

      <Modal.Root
        open={taxYearConfirmModal.isOpen}
        onOpenChange={taxYearConfirmModal.close}
      >
        <Modal.Content size="small">
          <StatusModal type="warning" title="Are you sure?">
            <p className="t-m-0 t-text-body-sm t-text-text-30">
              This will also update{" "}
              <span className="t-font-bold">{entityDetail.name}</span>
              's tax year end to{" "}
              <span className="t-font-bold">{pendingTaxYear?.label}</span> in
              Inkle Tax.
            </p>
          </StatusModal>
          <Modal.Footer className="t-flex t-items-center t-justify-end t-gap-3">
            <Button onClick={taxYearConfirmModal.close}>Cancel</Button>
            <Button
              customType="primary"
              onClick={handleConfirmTaxYearChange}
              isLoading={isUpdating}
              disabled={isUpdating}
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};

export const BookkeepingPreferences = ({
  booksMode,
  setBooksMode,
  fieldValues = [],
}: BookkeepingPreferencesProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId()!;
  const { alertToast } = useToast();

  const [updateField] = useUpdateStepFieldMutation();

  const bookingModeField = fieldValues.find(
    (f) => f.content === "BOOKKEEPING_MODE"
  );

  const handleBooksModeChange = async (mode: string) => {
    setBooksMode(mode);

    if (!bookingModeField?.uuid) return;

    try {
      await updateField({
        groupId,
        fieldId: bookingModeField.uuid,
        payload: { value: mode === "assisted" },
      }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const description = booksMode
    ? "Set up your Books preferences and configure key settings to match the way you work."
    : "Set up your Books preferences to match the way you work. Configure key settings that help you get the most out of the platform, from how your books are organised to what automations you'd like to enable.";

  return (
    <div className="t-max-w-[652px] t-mx-auto t-flex t-flex-col t-gap-6 t-py-10 sm:t-py-6">
      <div className="t-flex t-flex-col t-gap-2">
        <div className="t-text-subtext t-text-text-100">
          Customize Inkle Books
        </div>
        <div className="t-text-body-sm t-text-text-30">{description}</div>
      </div>

      <div className="t-flex t-flex-col t-gap-4">
        <label className="t-text-subtext">How do you want to do Books</label>

        <RadioGroup.Root
          onValueChange={handleBooksModeChange}
          value={booksMode}
          className="t-flex t-gap-4 t-items-start sm:t-flex-col sm:t-items-stretch"
        >
          <div className="t-flex t-flex-col t-gap-2 t-flex-1">
            <div className="t-border t-border-solid t-border-neutral-0 t-rounded-lg t-p-4 t-flex t-flex-col t-gap-1 t-transition t-flex-1">
              <RadioGroup.Item value="self" className="t-flex-1" align="start">
                <div className="t-text-subtext-sm t-text-text-60">
                  I will do myself
                </div>
                <div className="t-text-body-sm-lite t-text-text-30">
                  Do Books yourself or have your own accountant
                </div>
              </RadioGroup.Item>
            </div>
          </div>

          <div className="t-flex t-flex-col t-gap-2 t-flex-1">
            <div className="t-border t-border-solid t-border-neutral-0 t-rounded-lg t-p-4 t-flex t-flex-col t-gap-1 t-transition t-flex-1">
              <RadioGroup.Item
                value="assisted"
                className="t-flex-1"
                align="start"
              >
                <div className="t-text-subtext-sm t-text-text-60">
                  Inkle Bookkeeping
                </div>
                <div className="t-text-body-sm-lite t-text-text-30">
                  Inkle will handle categorization, reconciliation, prepare
                  finalized financials
                </div>
              </RadioGroup.Item>
            </div>
          </div>
        </RadioGroup.Root>
      </div>

      {booksMode === "self" && (
        <>
          <Divider />
          <BookkeepingSettings />
        </>
      )}

      {booksMode === "assisted" && (
        <>
          <Divider />
          <ShareBankAccountPreferencesContent
            entityId={entityId}
            groupId={groupId}
          />
        </>
      )}
    </div>
  );
};
